/**
 * Splitting a sentence for chunked synthesis.
 *
 * Pure, and the reason it is pure is that everything difficult about Piper is measurable *here* rather than in
 * the worker. Phase 0 settled the requirement with three numbers on this iPad:
 *
 *   10 words →  603 ms to first audio
 *   30 words → 1476 ms
 *   35 words → 2036 ms
 *
 * Piper has no streaming API — `run()` resolves with the whole utterance — so latency scales with length. The
 * budget is ~900 ms to first audio, which the 10-word case beats and the 35-word case misses by more than a
 * second. **Chunking is therefore not an optimisation, it is the only way long sentences ship.** Synthesise a
 * short first chunk, start speaking it, and synthesise the rest underneath: at RTF 0.19 a chunk takes about a
 * fifth of its own playback time to produce, so the audio never catches up with the synthesis.
 *
 * ── Why clause boundaries rather than a fixed word count ──
 *
 * A chunk boundary is a seam in the audio: two `AudioBuffer`s played back to back leave a small gap. At a comma
 * that gap is a **pause a listener expects**, and splitting there makes the seam inaudible for free — which is
 * also why this needs no audio-clock scheduling and no crossfade. Split mid-clause and the same gap sounds like
 * a stutter.
 *
 * So: prefer punctuation, fall back to a word cap, and never split a word.
 */

/**
 * Words per chunk, chosen from the measurement rather than by feel.
 *
 * Ten words is the case Phase 0 timed at 603 ms, leaving ~300 ms of headroom under the 900 ms budget for the
 * phonemiser, the tensor setup and the first buffer reaching the speaker. Raising it trades the opening pause
 * for fewer seams, and 12 words was already 1.2 s in the 30-word measurement's neighbourhood — so this is close
 * to the largest honest value.
 */
export const MAX_WORDS_PER_CHUNK = 10;

/**
 * Below this a chunk is a fragment — *unless* it ends at punctuation.
 *
 * The exemption is the important half, and it took a failing test to get it the right way round. "Ja," on its own
 * looks like the fragment this constant exists to prevent, and it is the opposite: the pause after it falls on a
 * comma, which is where a listener expects one, and a one-word opener means first audio in about a tenth of the
 * budget. What actually sounds broken is a short chunk whose seam lands **mid-clause** — "…bevor ich" followed by
 * "mich hinlege." — because there the gap is a stutter rather than a breath.
 *
 * So the rule is not "no short chunks". It is "no short chunks at seams a listener would not expect".
 */
const MIN_WORDS_PER_CHUNK = 3;

/** A seam here is a pause a listener already expects. */
const ENDS_AT_PUNCTUATION = /[,;:.!?…—–]$/u;

/**
 * Whether a short chunk is acceptable, which depends on the chunk **before** it and not on itself.
 *
 * The second thing I got backwards, and the test caught it too: "mich hinlege." ends in a full stop, so an
 * earlier version exempted it — but that full stop is the end of the utterance, not the seam. The gap in question
 * sits between "…bevor ich" and "mich hinlege.", and what makes it a stutter is that the *previous* chunk stopped
 * mid-clause. A chunk's own trailing punctuation says nothing about the silence in front of it.
 *
 * The first chunk has no seam before it at all, so a short opener is always fine — and is in fact the fastest
 * possible first audio.
 */
function shortChunkIsFine(chunk: string, previous: string | undefined): boolean {
  if (countWords(chunk) >= MIN_WORDS_PER_CHUNK) return true;
  if (previous === undefined) return true;
  return ENDS_AT_PUNCTUATION.test(previous);
}

/** Sentence-ending and clause-ending marks, in the order a reader would pause at them. */
const HARD_BOUNDARY = /([.!?…]+)\s+/u;
const SOFT_BOUNDARY = /([,;:]|\s—\s|\s–\s)\s*/u;

/**
 * Split text into chunks that can be synthesised one at a time.
 *
 * Returns the whole text as a single chunk when it is short enough, which is the common case: most utterances on
 * this device are a handful of words, and a single chunk has no seam at all.
 */
export function chunkForSynthesis(text: string, maxWords = MAX_WORDS_PER_CHUNK): string[] {
  const cleaned = text.replace(/\s+/gu, ' ').trim();
  if (cleaned === '') return [];
  if (countWords(cleaned) <= maxWords) return [cleaned];

  return accumulate(pieces(cleaned, maxWords), maxWords);
}

/**
 * The smallest units a seam may fall between: sentences, then clauses, then — only where there is no
 * punctuation to use — runs of words.
 */
function pieces(text: string, maxWords: number): string[] {
  const out: string[] = [];

  // Sentences first. A full stop is the strongest seam available and the one place a pause is always right.
  for (const sentence of splitKeeping(text, HARD_BOUNDARY)) {
    if (countWords(sentence) <= maxWords) {
      out.push(sentence);
      continue;
    }
    // Then clauses, which is where German long sentences actually bend.
    for (const clause of splitKeeping(sentence, SOFT_BOUNDARY)) {
      if (countWords(clause) <= maxWords) {
        out.push(clause);
        continue;
      }
      // Nothing to split on. Counting words leaves an audible seam mid-clause, and it still beats two seconds
      // of silence before the first sound.
      out.push(...splitByWords(clause, maxWords));
    }
  }

  return out;
}

/**
 * Pack pieces into as few chunks as the cap allows.
 *
 * ── The cap is strict for the first chunk and elastic afterwards ──
 *
 * That asymmetry is the whole design, and it took a failing test to see it. Time-to-first-audio depends on
 * **chunk 1 only** — every later chunk is synthesised while the previous one plays, and at RTF 0.19 there is
 * roughly five times more playback than synthesis to hide it in. So chunk 1 obeys the ten-word cap absolutely,
 * and a later chunk may run a few words over when the alternative is leaving a two-word fragment.
 *
 * A fragment is worse than a long chunk. "Ja," alone, or a trailing "mich hinlege.", puts a pause exactly where
 * a listener hears hesitation — and adding hesitation to somebody whose speech is already slow is the opposite
 * of what this feature is for.
 */
function accumulate(parts: string[], maxWords: number): string[] {
  const chunks: string[] = [];
  let current: string[] = [];
  let currentWords = 0;

  const flush = (): void => {
    if (current.length > 0) chunks.push(current.join(' '));
    current = [];
    currentWords = 0;
  };

  for (const piece of parts) {
    const pieceWords = countWords(piece);

    if (current.length === 0) {
      current = [piece];
      currentWords = pieceWords;
      continue;
    }

    const fits = currentWords + pieceWords <= maxWords;
    // Going over the cap is allowed only to rescue a chunk that would sound wrong where it lands. Chunk 1 never
    // needs it — nothing precedes it, so there is no seam to sound wrong.
    const rescuesFragment = !shortChunkIsFine(current.join(' '), chunks.at(-1));

    if (fits || rescuesFragment) {
      current.push(piece);
      currentWords += pieceWords;
      continue;
    }

    flush();
    current = [piece];
    currentWords = pieceWords;
  }
  flush();

  return foldTrailingFragment(chunks, maxWords);
}

/**
 * A last chunk under the minimum joins the one before it, even if that makes it long.
 *
 * The tail is the safest place to spend the cap: by the time it is synthesised the person has been speaking for
 * seconds and nobody is waiting on it.
 */
function foldTrailingFragment(chunks: string[], maxWords: number): string[] {
  if (chunks.length < 2) return chunks;

  const last = chunks.at(-1)!;
  const previous = chunks.at(-2)!;
  if (shortChunkIsFine(last, previous)) return chunks;
  // Not unbounded: a chunk twice the cap would be a long silence if it ever became the *first* one after a
  // future change here.
  if (countWords(previous) + countWords(last) > maxWords * 2) return chunks;

  return [...chunks.slice(0, -2), `${previous} ${last}`];
}

const countWords = (text: string): number => (text.trim() === '' ? 0 : text.trim().split(' ').length);

/**
 * Split on a boundary, keeping the punctuation attached to the chunk it ends.
 *
 * Attached rather than dropped because the phonemiser uses it: espeak-ng gives a comma its prosody, so a chunk
 * ending in "," is spoken with the falling intonation that makes the following gap sound intended.
 */
function splitKeeping(text: string, boundary: RegExp): string[] {
  const parts: string[] = [];
  let rest = text;

  for (;;) {
    const match = boundary.exec(rest);
    if (!match || match.index === undefined) break;

    const end = match.index + match[0].length;
    const piece = rest.slice(0, match.index) + (match[1] ?? '');
    if (piece.trim() !== '') parts.push(piece.trim());
    rest = rest.slice(end);
  }

  if (rest.trim() !== '') parts.push(rest.trim());
  return parts.length > 0 ? parts : [text];
}

function splitByWords(text: string, maxWords: number): string[] {
  const words = text.split(' ');
  const out: string[] = [];
  for (let index = 0; index < words.length; index += maxWords) {
    out.push(words.slice(index, index + maxWords).join(' '));
  }
  return out;
}
